import type { ServiceLine } from 'api/types/service.types';
import { useConfig } from 'context/app-config';
import { selectCurrentOrder, selectServiceLineDropOffs } from 'pages/newOrder/newOrder.selectors';
import { useDropOffTimeSlots } from 'pages/queries/timeslot.queries';
import * as React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { usePickupAndDropoff } from '../../hooks/usePickupAndDropoff';
import SelectPickupOrDropoff from '../SelectPickupOrDropoff';

interface ServiceLineBasedPickupOrDropoffProps {
  serviceLine?: ServiceLine;
  description?: string;
  serviceLineDependancy?: ServiceLine;
  action?: 'PICKUP' | 'DROPOFF';
}

const ServiceLineBasedDropoff = ({ action = 'PICKUP', serviceLine, serviceLineDependancy, description }: ServiceLineBasedPickupOrDropoffProps) => {
  const { addressId, pickupTimeslot, dropoffTimeslot: defaultDropoffTimeSlot } = useSelector(selectCurrentOrder);
  const config = useConfig();
  const serviceLineDropoffs = useSelector(selectServiceLineDropOffs);

  const { isDisabled } = usePickupAndDropoff({
    action,
    serviceLine,
    serviceLineDependancy,
  });

  const dropoffTimeslot = useMemo(
    () =>
      config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs && serviceLine ? serviceLineDropoffs[serviceLine] : defaultDropoffTimeSlot,
    [config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, defaultDropoffTimeSlot, serviceLine, serviceLineDropoffs]
  );

  const { data: dropoffTimeSlots, fetchStatus: dropoffFetchStatus } = useDropOffTimeSlots({
    addressId,
    pickupDate: pickupTimeslot?.date,
    serviceLine,
    enabled: Boolean(addressId && !dropoffTimeslot && !isDisabled),
  });

  return (
    <SelectPickupOrDropoff
      key={`${serviceLine}select-pickup-dropoff`}
      action="DROPOFF"
      description={description}
      serviceLine={serviceLine}
      serviceLineDependancy={serviceLineDependancy}
      timeSlotsData={dropoffTimeSlots?.dropoffTimeslotsList}
      isLoading={dropoffFetchStatus === 'fetching'}
    />
  );
};

export default ServiceLineBasedDropoff;
